import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchReviewQueue, submitReview, type ReviewItem } from "../api/reviews";
import { useAuth } from "../auth/AuthProvider";

const QUEUE_KEY = ["review-queue"];

export function ReviewQueue() {
  const { service } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUEUE_KEY,
    queryFn: () => fetchReviewQueue(service),
  });

  const review = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approve" | "reject" }) =>
      submitReview(service, id, decision),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUEUE_KEY }),
  });

  return (
    <main className="content">
      {isLoading && <p className="muted">Loading review queue…</p>}
      {isError && <p className="error">Could not load the queue: {(error as Error).message}</p>}
      {data && data.length === 0 && <p className="muted">Nothing awaiting review. 🎉</p>}

      <ul className="queue">
        {data?.map((item) => (
          <ReviewCard key={item.id} item={item} pending={review.isPending} onDecide={(decision) => review.mutate({ id: item.id, decision })} />
        ))}
      </ul>
    </main>
  );
}

function ReviewCard({
  item,
  pending,
  onDecide,
}: {
  item: ReviewItem;
  pending: boolean;
  onDecide: (decision: "approve" | "reject") => void;
}) {
  return (
    <li className="card review">
      <div className="review-image">
        {item.output_image_url ? (
          <img src={item.output_image_url} alt={`generated ${item.operation} for ${item.ring_sku}`} />
        ) : (
          <div className="no-image muted">no image</div>
        )}
      </div>
      <div className="review-meta">
        <div className="row">
          <span className="tag">{item.operation}</span>
          <span className="sku">{item.ring_sku}</span>
        </div>
        {item.target_metal && <div className="muted">→ {item.target_metal}</div>}
        <div className="muted small">{new Date(item.created_at).toLocaleString("en-GB")}</div>
        <div className="actions">
          <button className="btn approve" disabled={pending} onClick={() => onDecide("approve")}>
            Approve
          </button>
          <button className="btn reject" disabled={pending} onClick={() => onDecide("reject")}>
            Reject
          </button>
        </div>
      </div>
    </li>
  );
}
